import { Button, DeleteIcon, Heading, PlusIcon, Text } from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { type ReactNode, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { STEP_COLLAPSE } from './stepLayout'

type StepEntryCardProps = {
  /** What was recorded, as `StepEntryCardItem`s and `StepEntryCardRow`s. */
  children?: ReactNode
  /** Accessible name of the Delete button, naming what it deletes. */
  deleteLabel: string
  /** A line under the title. */
  description?: ReactNode
  /** Accessible name of the Edit button, naming what it edits. */
  editLabel: string
  /** What the entry still needs before the step can be saved. */
  error?: string
  icon: ReactNode
  onDelete: () => void
  onEdit: () => void
  testId: string
  title: ReactNode
}

/**
 * One recorded entry in a step, such as a restrictive practice, an injury or
 * an action: its icon and title, Edit and Delete, and what was recorded.
 */
const StepEntryCard = ({
  children,
  deleteLabel,
  description,
  editLabel,
  error,
  icon,
  onDelete,
  onEdit,
  testId,
  title
}: StepEntryCardProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const errorId = useId()

  return (
    <Card data-testid={testId}>
      <Icon data-testid={`${testId}-icon`}>{icon}</Icon>
      <Content>
        <Header data-testid={`${testId}-header`}>
          <TitleAndDescription>
            <Heading size="xs" as="h3" data-testid={`${testId}-title`}>
              {title}
            </Heading>
            {description && <Description>{description}</Description>}
          </TitleAndDescription>
          <Buttons>
            <Button
              variant="secondary"
              aria-label={editLabel}
              aria-describedby={error ? errorId : undefined}
              // Where a failed Next sends the focus: Edit is how it is fixed.
              data-focus-on-error={error ? true : undefined}
              data-testid={`${testId}-edit`}
              onClick={onEdit}
            >
              {t(incidentReportsConstants.CASE_V2_ENTRY_EDIT)}
            </Button>
            <DeleteButton
              variant="secondary"
              aria-label={deleteLabel}
              data-testid={`${testId}-delete`}
              icon={<DeleteIcon size={16} />}
              onClick={onDelete}
            />
          </Buttons>
        </Header>
        {error && (
          <Text id={errorId} tone="critical" data-testid={`${testId}-error`}>
            {error}
          </Text>
        )}
        {children && <Details>{children}</Details>}
      </Content>
    </Card>
  )
}

type StepEntryCardItemProps = {
  children: ReactNode
  label: ReactNode
  testId?: string
}

/** A label and what was recorded against it. */
export const StepEntryCardItem = ({
  children,
  label,
  testId
}: StepEntryCardItemProps) => (
  <div data-testid={testId}>
    <Text tone="secondary">{label}</Text>
    <ItemValue forwardedAs="div" weight="medium">
      {children}
    </ItemValue>
  </div>
)

type StepEntryListProps = {
  addLabel: ReactNode
  addTestId: string
  /** The entries' cards. */
  children?: ReactNode
  /** Leave out to hide Add, e.g. once the only allowed entry is recorded. */
  onAdd?: () => void
}

/**
 * A list of entries edited in a drawer: which entry it is open on (no index
 * while adding), and the edits the list makes. `Draft` is what the drawer
 * opens with, when that differs from an entry.
 */
export const useEntryDrawer = <Entry, Draft = Entry>(
  value: ReadonlyArray<Entry>,
  onChange: ((entries: Array<Entry>) => void) | undefined
) => {
  const [drawer, setDrawer] = useState<{ entry: Draft; index?: number }>()

  return {
    close: () => setDrawer(undefined),
    drawer,
    open: (entry: Draft, index?: number) => setDrawer({ entry, index }),
    remove: (index: number) =>
      onChange?.(value.filter((_, other) => other !== index)),
    save: (entry: Entry) => {
      onChange?.(
        drawer?.index === undefined
          ? [...value, entry]
          : value.map((saved, index) =>
              index === drawer.index ? entry : saved
            )
      )
      setDrawer(undefined)
    }
  }
}

/** A step's entry cards with "+ Add …" below them. */
export const StepEntryList = ({
  addLabel,
  addTestId,
  children,
  onAdd
}: StepEntryListProps) => (
  <List>
    {children}
    {onAdd && (
      <Add>
        <Button
          variant="transparent"
          icon={<PlusIcon size={16} />}
          data-testid={addTestId}
          onClick={onAdd}
        >
          {addLabel}
        </Button>
      </Add>
    )}
  </List>
)

const { color, radius, size, spacing } = cssVars

/** Items side by side in two columns, one per row on a narrow screen. */
export const StepEntryCardRow = styled.div`
  display: grid;
  gap: ${spacing[100]} ${spacing[200]};
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media ${STEP_COLLAPSE} {
    grid-template-columns: minmax(0, 1fr);
    row-gap: ${spacing[200]};
  }
`

const Card = styled.div`
  align-items: flex-start;
  background: ${color.bg.primary};
  border: 1px solid ${color.border.subtle};
  border-radius: ${radius[100]};
  display: flex;
  gap: ${spacing[100]};
  padding: ${spacing[150]} ${spacing[200]} ${spacing[150]} ${spacing[150]};
`

const Icon = styled.div`
  display: flex;
  flex-shrink: 0;

  @media ${STEP_COLLAPSE} {
    display: none;
  }
`

const Content = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${spacing[100]};
  min-width: 0;
  overflow-wrap: anywhere;
`

const Header = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${spacing[150]};

  @media ${STEP_COLLAPSE} {
    align-items: stretch;
    flex-direction: column-reverse;
  }
`

const TitleAndDescription = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${spacing[50]};
  min-width: 0;
`

const Description = styled(Text)`
  white-space: pre-wrap;
`

/** Keeps the line breaks in free text, as the overview does. */
const ItemValue = styled(Text)`
  white-space: pre-wrap;
`

const Buttons = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: ${spacing[100]};
  justify-content: flex-end;
`

/** Outlined like Edit beside it, square, with the bin in red. */
const DeleteButton = styled(Button)`
  color: ${color.icon.action.critical.default};
  padding-inline: 0;
  width: ${size[500]};

  &:hover {
    color: ${color.icon.action.critical.hover};
  }
`

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[200]};
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[200]};
`

const Add = styled.div`
  display: flex;
`

export default StepEntryCard
